import { useEffect, useRef, useState } from "react"
import LoaderStyle from "./Loader.module.scss"

const easeInOutCubic = (t: number) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2

interface Props {
  setIsLoaded: React.Dispatch<React.SetStateAction<boolean>>
}

const Loader = ({ setIsLoaded }: Props): React.JSX.Element | null => {
  const DURATION = 3000
  const FADE_DURATION = 600
  const MIN_INTERVAL = 300 // toutes les 0.3s
  const startRef = useRef(performance.now())
  const rafRef = useRef<number>()
  const minIntervalRef = useRef<NodeJS.Timeout>()
  const [progress, setProgress] = useState(0)
  const [fadeOut, setFadeOut] = useState(false)
  const [visible, setVisible] = useState(true)

  useEffect(() => {
    const animate = (time: number) => {
      const elapsed = time - startRef.current
      const t = Math.min(elapsed / DURATION, 1)
      const eased = easeInOutCubic(t)
      setProgress((prev) => Math.max(prev, eased * 100))

      if (t < 1) {
        rafRef.current = requestAnimationFrame(animate)
      } else {
        setFadeOut(true)
        clearInterval(minIntervalRef.current)
        setTimeout(() => {
          setVisible(false)
          setIsLoaded(true)
        }, FADE_DURATION)
      }
    }

    minIntervalRef.current = setInterval(() => {
      setProgress((prev) => Math.min(prev + 1, 100))
    }, MIN_INTERVAL)

    rafRef.current = requestAnimationFrame(animate)

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      clearInterval(minIntervalRef.current)
    }
  }, [setIsLoaded])

  if (!visible) return null

  const shown = Math.min(100, Math.round(progress))
  const shownStr = shown.toString().padStart(3, "0")

  return (
    <div
      className={`${LoaderStyle.loader} ${fadeOut ? LoaderStyle.fadeOut : ""}`}
    >
      <div className={LoaderStyle.counter}>{shownStr}</div>
    </div>
  )
}

export default Loader
